<!-- Component for displaying a list of products and their details -->
<template>
	<div class="flex h-full px-10 pt-14">
		<!-- Sidebar dropdown -->
		<div class="w-1/5 overflow-y-auto border-r border-slate-200 pr-8">
			<ProductDropdown
				v-model="selectedProduct"
				:productPlans="productPlans"
				:productType="props.blok?.productType"
			/>
		</div>

		<!-- Main content (product details) -->
		<div class="w-4/5 pl-10">
			<FormProduct
				v-if="selectedProduct"
				class="h-full items-center"
				twoColumns
				:product="selectedProduct"
				:key="selectedProduct?.id"
			/>
		</div>

		<button
			v-if="advanceButtonText"
			type="button"
			@click="onAdvanceButtonClick"
			class="bg-accent-400 text-accent-700 hover:bg-accent-700 absolute right-8 bottom-8 flex flex-row items-center gap-2 rounded-lg px-10 py-2 font-sans text-base leading-loose font-bold hover:text-white"
		>
			{{ advanceButtonText }} <ArrowLongRightIcon class="size-6" />
		</button>
	</div>
</template>

<script setup lang="ts">
import type { PbLineItem } from '@/apiTypes'
import type { IndexedPlan } from '@/classes/Plan'
import type { PbLineItemProductAndPricingOptions } from '@/classes/PriceBook'
import FormProduct from '@/components/merchandise/FormProduct.vue'
import ProductDropdown from '@/components/products/ProductDropdown.vue'
import { useMerchandise } from '@/mixins/merchandise'
import type { ProductsSlideStoryblok } from '@/sbTypes'
import { usePresentationController } from '@/stores/presentationController'
import { useSessionStore } from '@/stores/session'
import { ArrowLongRightIcon } from '@heroicons/vue/16/solid'
import { ref, computed, type PropType } from 'vue'

const props = defineProps({
	blok: Object as PropType<ProductsSlideStoryblok>,
})

const { priceBook, isLineItemInPlan } = useMerchandise()

const sessionStore = useSessionStore()
const controller = usePresentationController()

const plans = computed(() => {
	return sessionStore.activePlans
})

// Only if all plans in session selected a Burial package, skip urn
const allPlanHasBurial = computed(() => {
	return plans.value.every((plan) =>
		plan.hasBurialPackage(priceBook.value?.packages ?? [])
	)
})

/** The text to show on the advance button */
const advanceButtonText = computed(() => {
	// starting from PSS-1509, per requirement if all plans in the appointment only selected Burial
	// the presentation should go directly to vault slide from the casket slide
	if (props.blok?.productType === 'casket' && allPlanHasBurial.value) {
		const vaultSlide = controller.allSlides?.find(
			(slide) =>
				slide.component === 'products-slide' &&
				(slide as ProductsSlideStoryblok).productType === 'vault'
		)

		if (vaultSlide) {
			return vaultSlide.incomingButtonText ?? vaultSlide.title ?? 'Next'
		}
	}

	// else, take the incomingButtonText from the next slide and display it on the advance button
	return (
		controller.getNextSlide()?.incomingButtonText ??
		controller.getNextSlide()?.title ??
		'Next'
	)
})

/**
 * Map products and packages to their corresponding plans.
 */
const productPlans = computed<Record<string, IndexedPlan[]>>(() => {
	if (!priceBook.value?.products && !priceBook.value?.packages) return {}

	const items: PbLineItem[] = [
		...(priceBook.value?.products ?? []),
		...(priceBook.value?.packages ?? []),
	]

	return items.reduce<Record<string, IndexedPlan[]>>((acc, item) => {
		acc[item.id] = (plans.value ?? [])
			.map(
				(plan, index): IndexedPlan => ({
					index,
					plan,
				})
			)
			.filter((indexedPlan) => isLineItemInPlan(item, indexedPlan.plan))

		return acc
	}, {})
})

// Reactive ref for the selected product
const selectedProduct = ref<PbLineItemProductAndPricingOptions | undefined>(
	undefined
)

/**
 * Advance to the next slide after the user has selected a product.
 * For urns, go to the essentials slide if it exists, otherwise go to the next slide.
 * For caskets, go to the vault slide if it exists, otherwise go to the next slide.
 */
function onAdvanceButtonClick() {
	// We have a special case where if they are in burial package we skip from casket to vault
	if (props.blok?.productType === 'casket' && allPlanHasBurial.value) {
		const vaultSlide = controller.allSlides?.find(
			(slide) =>
				slide.component === 'products-slide' &&
				(slide as ProductsSlideStoryblok).productType === 'vault'
		)

		if (vaultSlide) {
			controller.gotoSlideId(vaultSlide._uid)
			return
		}
	}

	controller.gotoNextSlide()
}
</script>
